import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  exchangeCodeForTokens, 
  parseIdToken, 
  refreshAccessToken,
  getLogoutUrl 
} from '../services/authService';
import { api } from '@/services/api';

export interface User {
  userId: string;
  email: string;
  nickname: string;
  profileImageUrl?: string;
  avatarUrl?: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isGuest: boolean; // 게스트 모드 (관전만 가능)
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  setUser: (user: User) => void;
  setTokens: (accessToken: string, idToken: string, refreshToken?: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setAdmin: (isAdmin: boolean) => void;
  setGuest: (isGuest: boolean) => void;
  enterAsGuest: () => void;
  checkAdminStatus: () => Promise<void>;
  logout: () => void;
  initialize: () => void;
  handleOAuthCallback: (code: string) => Promise<void>;
  refreshSession: () => Promise<boolean>;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  accessToken: null,
  idToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isGuest: false,
  isAdmin: false,
  isLoading: true,
  error: null,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setUser: (user) => {
        set({ user, isAuthenticated: true, error: null });
      },

      setTokens: (accessToken, idToken, refreshToken) => {
        set({ 
          accessToken, 
          idToken,
          ...(refreshToken && { refreshToken })
        });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setError: (error) => {
        set({ error });
      },

      setAdmin: (isAdmin) => {
        set({ isAdmin });
      },

      setGuest: (isGuest) => {
        set({ isGuest });
      },

      // 게스트로 입장 (관전만 가능)
      enterAsGuest: () => {
        set({
          user: {
            userId: `guest_${Date.now()}`,
            email: '',
            nickname: '게스트',
          },
          isAuthenticated: false, // 게스트는 미인증 상태 유지
          isGuest: true,
          isLoading: false,
        });
      },

      checkAdminStatus: async () => {
        const { accessToken } = get();
        if (!accessToken) {
          set({ isAdmin: false });
          return;
        }
        
        try {
          const response = await api.get<{ isAdmin: boolean; email: string }>('/admin/check');
          console.log('[Auth] Admin check response:', response);
          set({ isAdmin: response.isAdmin || false });
        } catch (error) {
          console.error('Admin check error:', error);
          set({ isAdmin: false });
        }
      },

      logout: () => {
        const { isGuest } = get();
        set({
          user: null,
          accessToken: null,
          idToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isGuest: false,
          isAdmin: false,
          error: null,
        });
        // 게스트가 아닌 경우에만 Cognito 로그아웃
        if (!isGuest) {
          window.location.href = getLogoutUrl();
        }
      },

      initialize: () => {
        const { accessToken, user, isGuest, checkAdminStatus } = get();
        
        // 토큰이 있고 유저 정보가 있으면 인증된 사용자
        if (accessToken && user && !isGuest) {
          set({ isAuthenticated: true, isGuest: false, isLoading: false });
          checkAdminStatus();
        } 
        // 게스트 상태
        else if (isGuest && user) {
          set({ isAuthenticated: false, isLoading: false });
        } 
        // 그 외 (비로그인)
        else {
          set({ isAuthenticated: false, isGuest: false, isLoading: false });
        }
      },

      // OAuth 콜백 처리 (authorization code → tokens → user)
      handleOAuthCallback: async (code: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // 1. Code를 토큰으로 교환
          const tokens = await exchangeCodeForTokens(code);
          
          // 2. ID 토큰에서 사용자 정보 파싱
          const cognitoUser = parseIdToken(tokens.id_token);
          
          // 3. User 객체 생성
          const user: User = {
            userId: cognitoUser.sub,
            email: cognitoUser.email,
            nickname: cognitoUser.nickname || cognitoUser.name || cognitoUser.email.split('@')[0],
            profileImageUrl: cognitoUser.picture,
          };
          
          // 4. 상태 업데이트 (게스트 상태 리셋 포함)
          set({
            user,
            accessToken: tokens.access_token,
            idToken: tokens.id_token,
            refreshToken: tokens.refresh_token || null,
            isAuthenticated: true,
            isGuest: false, // 로그인 시 게스트 상태 해제
            isLoading: false,
            error: null,
          });
          
          // 5. 관리자 여부 확인
          get().checkAdminStatus();
          
        } catch (error) {
          console.error('OAuth callback error:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : '로그인에 실패했습니다',
          });
          throw error;
        }
      },

      // 토큰 갱신
      refreshSession: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return false;
        
        try {
          const tokens = await refreshAccessToken(refreshToken);
          set({
            accessToken: tokens.access_token,
            idToken: tokens.id_token,
          });
          return true;
        } catch (error) {
          console.error('Token refresh error:', error);
          // 갱신 실패 시 로그아웃
          set({
            user: null,
            accessToken: null,
            idToken: null,
            refreshToken: null,
            isAuthenticated: false,
          });
          return false;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        idToken: state.idToken,
        refreshToken: state.refreshToken,
        isAdmin: state.isAdmin,
        isGuest: state.isGuest,
      }),
    }
  )
);
